import { Link } from "react-router-dom";
import { Github, Twitter, Linkedin, Instagram, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "../lib/site";

const SOCIALS = [
  { icon: Github, label: "GitHub", href: "https://github.com", color: "#0A0A14" },
  { icon: Twitter, label: "Twitter", href: "https://twitter.com", color: "#0096C7" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com", color: "#2563EB" },
  { icon: Instagram, label: "Instagram", href: "https://instagram.com", color: "#E0146B" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t border-ink/10 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Inubaan Labs" className="w-24 h-24 object-contain" />
            <span className="font-display font-bold text-lg tracking-tight text-ink">
              INUBAAN<span className="text-magenta">.</span>LABS
            </span>
          </div>
          <p className="mt-6 text-slate-600 text-base leading-relaxed max-w-sm">
            Building powerful apps. Solving real problems. Impacting lives —
            we turn today's ideas into tomorrow's impact.
          </p>
          <div className="mt-8 flex gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                aria-label={s.label}
                style={{ "--c": s.color }}
                className="w-10 h-10 rounded-full border border-ink/10 flex items-center justify-center text-slate-600 transition-colors duration-300 hover:border-[color:var(--c)] hover:text-[var(--c)]"
              >
                <s.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">Studio</p>
          <ul className="mt-6 space-y-4">
            {[
              { to: "/services", label: "Services" },
              { to: "/work", label: "Work" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-nav-${l.label.toLowerCase()}`}
                  className="text-slate-600 text-sm transition-colors duration-300 hover:text-cyan"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">Get in touch</p>
          <Link
            to="/contact"
            data-testid="footer-contact-button"
            className="group mt-6 inline-flex items-center gap-2 text-white font-semibold rounded-full pl-6 pr-5 py-3.5 bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right"
          >
            Contact us
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
          <p className="mt-4 text-slate-500 text-sm">
            Ideas today. Impact tomorrow.
          </p>
        </div>
      </div>

      <div className="mt-16 select-none pointer-events-none" aria-hidden="true">
        <p className="font-display font-black text-[13.5vw] leading-none tracking-tighter text-outline text-center whitespace-nowrap">
          INUBAAN
        </p>
      </div>

      <div className="mt-10 pt-8 border-t border-ink/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-mono text-xs text-slate-500 tracking-wider">
          © {new Date().getFullYear()} INUBAAN LABS. ALL RIGHTS RESERVED.
        </p>
        <p className="font-mono text-xs tracking-wider brand-gradient-text font-medium">
          IDEAS TODAY · IMPACT TOMORROW
        </p>
      </div>
    </div>
  </footer>
);
